import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Select from 'react-select';
import { useGetUserQuery, useCreateUserMutation, useUpdateUserMutation, useGetCountriesQuery, useGetCitiesQuery } from '../../../store/api/adminApi';
import PageLoader from '../../../components/PageLoader';
import ImageUpload from '../../../components/ImageUpload';
import { showSuccess, showError } from '../../../utils/toast';

const AdminUserForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  
  const { data, isLoading } = useGetUserQuery(id!, { skip: !isEdit });
  const [createUser, { isLoading: isCreating }] = useCreateUserMutation();
  const [updateUser, { isLoading: isUpdating }] = useUpdateUserMutation();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    country_id: null as number | null,
    city_id: null as number | null,
    picture: null as File | null,
  });

  const { data: countriesData } = useGetCountriesQuery({ per_page: 1000 });
  const { data: citiesData } = useGetCitiesQuery(
    { per_page: 1000, country_id: formData.country_id },
    { skip: !formData.country_id }
  );

  const [existingImage, setExistingImage] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (isEdit && data?.data) {
      const user = data.data;
      setFormData({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        country_id: user.country_id || null,
        city_id: user.city_id || null,
        picture: null,
      });
      // Extract filename from full URL if needed
      if (user.picture) {
        const imageFilename = user.picture.includes('/uploads/') 
          ? user.picture.split('/uploads/')[1] 
          : user.picture;
        setExistingImage(imageFilename);
      } else {
        setExistingImage(undefined);
      }
    }
  }, [isEdit, data]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCountryChange = (option: any) => {
    setFormData(prev => ({ ...prev, country_id: option ? option.value : null, city_id: null }));
  };

  const handleCityChange = (option: any) => {
    setFormData(prev => ({ ...prev, city_id: option ? option.value : null }));
  };

  const handleImageChange = (file: File | null) => {
    setFormData(prev => ({ ...prev, picture: file }));
  };

  const handleRemoveExistingImage = () => {
    setExistingImage(undefined);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const submitData = new FormData();
      submitData.append('name', formData.name);
      submitData.append('email', formData.email);
      submitData.append('phone', formData.phone);
      if (formData.country_id) submitData.append('country_id', String(formData.country_id));
      if (formData.city_id) submitData.append('city_id', String(formData.city_id));
      
      // Append picture if a new one is selected
      if (formData.picture) {
        submitData.append('picture', formData.picture);
      }

      if (isEdit) {
        const result = await updateUser({ id: parseInt(id!), data: submitData }).unwrap();
        showSuccess(result.message || 'User updated successfully!');
      } else {
        const result = await createUser(submitData).unwrap();
        showSuccess(result.message || 'User created successfully!');
      }
      
      navigate('/admin/users');
    } catch (err: any) {
      showError(err?.data?.message || `Failed to ${isEdit ? 'update' : 'create'} user. Please try again.`);
    }
  };

  if (isLoading) {
    return <PageLoader />;
  }

  return (
    <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
      <div className="d-flex flex-column flex-column-fluid">
        <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
          <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
            <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
              <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
                {isEdit ? 'Edit User' : 'Add User'}
              </h1>
              <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                <li className="breadcrumb-item text-muted">
                  <a href="#" onClick={(e) => { e.preventDefault(); navigate('/admin/dashboard'); }} className="text-muted text-hover-primary">Home</a>
                </li>
                <li className="breadcrumb-item">
                  <span className="bullet bg-gray-400 w-5px h-2px"></span>
                </li>
                <li className="breadcrumb-item text-muted">
                  <a href="#" onClick={(e) => { e.preventDefault(); navigate('/admin/users'); }} className="text-muted text-hover-primary">Users</a>
                </li>
              </ul>
            </div>
          </div>
        </div>
        
        <div id="kt_app_content" className="app-content flex-column-fluid">
          <div id="kt_app_content_container" className="app-container container-xxl">
            <div className="card">
              <div className="card-body pt-0">
                <form onSubmit={handleSubmit} id="kt_modal_new_target_form" className="form fv-plugins-bootstrap5 fv-plugins-framework">
                  <div className="row g-9 my-3">
                    <div className="col-md-6 fv-row fv-plugins-icon-container">
                      <label className="required fs-6 fw-semibold mb-2"> Name</label>
                      <input
                        type="text"
                        className="form-control form-control-solid"
                        placeholder="Enter Package Name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    
                    <div className="col-md-6 fv-row fv-plugins-icon-container">
                      <label className="required fs-6 fw-semibold mb-2">Email</label>
                      <input
                        type="email"
                        className="form-control form-control-solid"
                        placeholder="Enter User Email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    
                    <div className="col-md-6 fv-row fv-plugins-icon-container">
                      <label className="required fs-6 fw-semibold mb-2">Phone</label>
                      <input
                        type="number"
                        className="form-control form-control-solid"
                        placeholder="Enter Phone No."
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    
                    <div className="col-md-6 fv-row fv-plugins-icon-container">
                      <label className="fs-6 fw-semibold mb-2">Country</label>
                      <Select
                        isClearable
                        isSearchable
                        options={(countriesData?.data?.data || countriesData?.data || []).map((c: any) => ({ value: c.id, label: c.name || c.name_en }))}
                        value={formData.country_id ? { value: formData.country_id, label: (countriesData?.data?.data || countriesData?.data || []).find((c: any) => c.id === formData.country_id)?.name || String(formData.country_id) } : null}
                        onChange={handleCountryChange}
                        placeholder="Select Country"
                        className="react-select-container"
                        classNamePrefix="react-select"
                      />
                    </div>

                    <div className="col-md-6 fv-row fv-plugins-icon-container">
                      <label className="fs-6 fw-semibold mb-2">City</label>
                      <Select
                        isClearable
                        isSearchable
                        isDisabled={!formData.country_id}
                        options={(citiesData?.data?.data || citiesData?.data || []).map((c: any) => ({ value: c.id, label: c.name || c.name_en }))}
                        value={formData.city_id ? { value: formData.city_id, label: (citiesData?.data?.data || citiesData?.data || []).find((c: any) => c.id === formData.city_id)?.name || String(formData.city_id) } : null}
                        onChange={handleCityChange}
                        placeholder={formData.country_id ? 'Select City' : 'Select a country first'}
                        className="react-select-container"
                        classNamePrefix="react-select"
                      />
                    </div>
                    
                  
                    
                    <div className="col-md-6 fv-row fv-plugins-icon-container">
                      <ImageUpload
                        existingImage={existingImage}
                        onImageChange={handleImageChange}
                        onRemoveExisting={handleRemoveExistingImage}
                        accept=".png, .jpg, .jpeg"
                        label="Upload Picture"
                        name="picture"
                      />
                    </div>
                  </div>
                  
                  <div className="text-center">
                    <button type="button" id="kt_modal_new_target_cancel" className="btn btn-light me-3" onClick={() => navigate('/admin/users')}>
                      Discard
                    </button>
                    <button type="submit" id="kt_modal_new_target_submit" className="btn btn-base" disabled={isCreating || isUpdating}>
                      <span className="indicator-label">Submit</span>
                      {(isCreating || isUpdating) && (
                        <span className="indicator-progress">
                          Please wait...
                          <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                        </span>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminUserForm;

